import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import ContactForm from '../components/ContactForm';
import { 
  FaDesktop, 
  FaShieldAlt, 
  FaNetworkWired, 
  FaHeadset,
  FaCloud,
  FaServer,
  FaSyncAlt,
  FaCogs
} from 'react-icons/fa';
import bgImage from '../assets/managedit.jpg';

const ManagedITPage = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  const itServices = [
    {
      icon: <FaDesktop />,
      title: 'Endpoint Management',
      description: 'Comprehensive management of all endpoints including patching, software deployment, and remote troubleshooting',
      color: 'linear-gradient(135deg, #3b82f6, #0ea5e9)'
    },
    {
      icon: <FaShieldAlt />,
      title: 'Cybersecurity Services',
      description: '24/7 threat monitoring, vulnerability assessments, and enterprise-grade security solutions',
      color: 'linear-gradient(135deg, #ef4444, #f97316)'
    },
    {
      icon: <FaNetworkWired />,
      title: 'Network Management',
      description: 'Proactive monitoring, optimization, and maintenance of your network infrastructure',
      color: 'linear-gradient(135deg, #10b981, #14b8a6)'
    },
    {
      icon: <FaCloud />,
      title: 'Cloud Management',
      description: 'Full lifecycle management of your cloud environments across AWS, Azure, and GCP',
      color: 'linear-gradient(135deg, #8b5cf6, #d946ef)'
    },
    {
      icon: <FaServer />,
      title: 'Data Center Services',
      description: 'Hybrid infrastructure management with 99.99% uptime SLA guarantees',
      color: 'linear-gradient(135deg, #6366f1, #8b5cf6)'
    },
    {
      icon: <FaHeadset />,
      title: 'Help Desk Support',
      description: '24/7/365 IT support with guaranteed response times and resolution SLAs',
      color: 'linear-gradient(135deg, #f59e0b, #f97316)'
    },
    {
      icon: <FaSyncAlt />,
      title: 'Backup & Disaster Recovery',
      description: 'Automated backups and tested recovery plans to ensure business continuity',
      color: 'linear-gradient(135deg, #ec4899, #f43f5e)'
    },
    {
      icon: <FaCogs />,
      title: 'IT Process Automation',
      description: 'Workflow automation and ITSM integration to streamline operations',
      color: 'linear-gradient(135deg, #06b6d4, #0ea5e9)'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10
      }
    }
  };

  return (
    <div 
      style={{
        minHeight: '100vh',
        padding: '80px 20px',
        color: '#fff',
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
        backgroundAttachment: 'fixed',
        backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.92), rgba(15, 23, 42, 0.95)), url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        textAlign: 'center'
      }}
      ref={ref}
    >
      <motion.div
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={containerVariants}
        style={{ maxWidth: '1280px', margin: '0 auto' }}
      >
        <motion.h1 
          style={{
                 fontSize: 'clamp(2.25rem, 3vw + 1rem, 3.5rem)',
            fontWeight: '700',
            marginBottom: '24px',
            background: 'linear-gradient(to right, #3b82f6, #0ea5e9)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
          variants={itemVariants}
        >
          Managed IT Services
        </motion.h1>

        <motion.p 
          style={{
            fontSize: '1.25rem',
            maxWidth: '800px',
            margin: '0 auto 60px',
            color: '#d1d5db',
            lineHeight: '1.7'
          }}
          variants={itemVariants}
        >
          Proactive, enterprise-grade IT management that <span style={{ fontWeight: '600', color: '#fff' }}>reduces costs, improves security, and ensures business continuity</span>.
        </motion.p>

        <motion.div 
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '30px',
            maxWidth: '1200px',
            margin: '0 auto'
          }}
          variants={containerVariants}
        >
          {itServices.map((service, index) => (
            <motion.div
              key={index}
              style={{
                background: service.color,
                borderRadius: '16px',
                padding: '30px',
                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3)',
                color: '#fff',
                textAlign: 'left',
                transition: 'all 0.3s ease',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}
              variants={itemVariants}
              whileHover={{ 
                y: -8,
                scale: 1.03,
                boxShadow: '0 15px 30px rgba(0, 0, 0, 0.4)'
              }}
            >
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.5rem',
                marginBottom: '20px',
                background: 'rgba(255, 255, 255, 0.15)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)'
              }}>
                {service.icon}
              </div>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                marginBottom: '15px'
              }}>{service.title}</h3>
              <p style={{
                fontSize: '1rem',
                color: '#e5e7eb',
                lineHeight: '1.6'
              }}>{service.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* NovaCrest IT Services Section */}
        <motion.div 
          style={{
            marginTop: '80px',
            background: 'linear-gradient(to right, rgba(30, 41, 59, 0.8), rgba(15, 23, 42, 0.9))',
            borderRadius: '24px',
             padding: 'clamp(30px, 5vw, 50px)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3)',
            maxWidth: '1000px',
               maxWidth: 'min(1000px, 90vw)',
            margin: '80px auto 0',
             boxSizing: 'border-box',
             width: '100%'
          }}
          variants={itemVariants}
          whileHover={{ scale: 1.01 }}
        >
          <h2 style={{
            fontSize: '2.5rem',
            fontWeight: '700',
            marginBottom: '24px',
            background: 'linear-gradient(to right, #3b82f6, #0ea5e9)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textAlign: 'center'
          }}>
            NovaCrest IT Expertise
          </h2>
          
          <p style={{
            fontSize: '1.2rem',
            color: '#d1d5db',
            maxWidth: '800px',
            margin: '0 auto 40px',
            lineHeight: '1.7',
            textAlign: 'center'
          }}>
            Enterprise IT solutions that deliver reliability, security, and performance at scale.
          </p>
          
          <ul style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '20px',
            maxWidth: '900px',
            margin: '0 auto',
            padding: '0',
            listStyle: 'none'
          }}>
            {[
              "24/7/365 monitoring with guaranteed response times",
              "Certified engineers across all major technologies",
              "Average 99.99% uptime across all managed services",
              "SOC 2 Type II compliant security operations",
              "Proactive maintenance and patch management",
              "Comprehensive reporting and analytics dashboards",
              "Flexible pricing models (per-device, per-user, or flat-rate)",
              "Strategic IT roadmap planning and consulting",
              "Vendor management and procurement services",
              "Dedicated account managers and quarterly reviews"
            ].map((item, index) => (
              <motion.li
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  padding: '16px',
                  background: 'rgba(30, 41, 59, 0.5)',
                  borderRadius: '12px',
                  borderLeft: '4px solid #3b82f6',
                  transition: 'all 0.3s ease'
                }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ 
                  opacity: 1, 
                  x: 0,
                  transition: { delay: 0.5 + index * 0.1 }
                }}
                whileHover={{ x: 5 }}
              >
                <span style={{
                  display: 'inline-block',
                  marginRight: '12px',
                  color: '#3b82f6',
                  fontSize: '1.5rem'
                }}>•</span>
                <span style={{
                  color: '#e5e7eb',
                  lineHeight: '1.6'
                }}>{item}</span>
              </motion.li>
            ))}
          </ul>
        </motion.div>
        <ContactForm />
      </motion.div>
    </div>
  );
};

export default ManagedITPage;